import React from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    School as SchoolIcon,
    Group as GroupIcon,
    Assessment as AssessmentIcon,
    Security as SecurityIcon
} from '@mui/icons-material';

const About = () => {
    const features = [
        {
            icon: <SchoolIcon />,
            title: 'Comprehensive Learning',
            description: 'Access a wide range of medical topics and subjects through our structured courses and materials.'
        },
        {
            icon: <GroupIcon />,
            title: 'Community Driven',
            description: 'Join a community of medical professionals and students to share knowledge and experiences.'
        },
        {
            icon: <AssessmentIcon />,
            title: 'Practice Exams',
            description: 'Test your knowledge with our comprehensive practice exams and track your progress.'
        },
        {
            icon: <SecurityIcon />,
            title: 'Secure Platform',
            description: 'Your data and progress are protected with industry-standard security measures.'
        }
    ];

    const benefits = [
        'Access to high-quality medical content',
        'Interactive learning experience',
        'Progress tracking and analytics',
        'Mobile-friendly platform',
        'Regular content updates',
        'Expert-curated materials'
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: 8,
                    mb: 6
                }}
            >
                <Container maxWidth="lg">
                    <Typography variant="h2" component="h1" gutterBottom>
                        About Our Platform
                    </Typography>
                    <Typography variant="h5" sx={{ maxWidth: '800px' }}>
                        Empowering medical professionals and students with comprehensive learning resources and tools for professional development.
                    </Typography>
                </Container>
            </Box>

            {/* Mission Section */}
            <Container maxWidth="lg" sx={{ mb: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    Our Mission
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                    We are dedicated to providing high-quality medical education resources to healthcare professionals and students worldwide. Our platform combines cutting-edge technology with expert-curated content to create an engaging and effective learning experience.
                </Typography>

                {/* Features Grid */}
                <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
                    Key Features
                </Typography>
                <Grid 
                    container 
                    spacing={4} 
                    sx={{ 
                        mb: 8,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center'
                    }}
                >
                    {features.map((feature, index) => (
                        <Grid 
                            item 
                            xs={12} 
                            sm={6} 
                            md={4} 
                            key={index}
                            sx={{ 
                                display: 'flex'
                            }}
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 4,
                                    width: '300px',
                                    height: '300px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    borderRadius: 2,
                                    transition: 'transform 0.3s, box-shadow 0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: 6
                                    }
                                }}
                            >
                                <Box 
                                    sx={{ 
                                        color: 'primary.main', 
                                        mb: 3,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.light',
                                        '& svg': {
                                            fontSize: 30,
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {feature.icon}
                                </Box>
                                <Typography 
                                    variant="h6" 
                                    component="h3" 
                                    gutterBottom 
                                    sx={{ 
                                        fontWeight: 'bold',
                                        mb: 2
                                    }}
                                >
                                    {feature.title}
                                </Typography>
                                <Typography 
                                    color="text.secondary"
                                    sx={{ 
                                        lineHeight: 1.6,
                                        flex: 1
                                    }}
                                >
                                    {feature.description}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Benefits Section */}
                <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4 }}>
                    Platform Benefits
                </Typography>
                <Grid 
                    container 
                    spacing={4} 
                    sx={{ 
                        display: 'flex',
                        alignItems: 'stretch'
                    }}
                >
                    <Grid 
                        item 
                        xs={12} 
                        md={6}
                        sx={{ display: 'flex' }}
                    >
                        <Paper 
                            elevation={3} 
                            sx={{ 
                                p: 4, 
                                width: '100%',
                                borderRadius: 2
                            }}
                        >
                            <List sx={{ p: 0 }}>
                                {benefits.map((benefit, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={benefit} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    <Grid 
                        item 
                        xs={12} 
                        md={6}
                        sx={{ display: 'flex' }}
                    >
                        <Paper 
                            elevation={3} 
                            sx={{ 
                                p: 4, 
                                width: '100%',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                Join Our Community
                            </Typography>
                            <Typography paragraph>
                                Become part of our growing community of medical professionals and students. Share your knowledge, learn from others, and advance your medical career with our comprehensive learning platform.
                            </Typography>
                            <Typography>
                                Start your learning journey today and take the next step in your medical education.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default About; 